// utils/s3Helper.js - Updated to use pre-signed URLs while keeping existing interface
import axios from 'axios';

/**
 * S3Helper class that maintains existing interface but uses pre-signed URLs
 * This ensures existing code continues to work without changes
 */
export class S3Helper {
  constructor(config = {}) {
    this.config = config;
    this.apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
    this.initialized = false;
  }

  /**
   * Initialize method - keeps existing interface
   */
  static initialize(customConfig = null) {
    if (!this.instance) {
      this.instance = new S3Helper(customConfig);
    }
    this.instance.initialized = true;
    return this.instance;
  }

  /**
   * Get singleton instance
   */
  static getInstance() {
    if (!this.instance) {
      this.instance = new S3Helper();
    }
    return this.instance;
  }

  /**
   * Generate pre-signed URL from backend
   */

  async getPresignedUrl(filename, contentType, path, data) {
    try {
      console.log('Getting pre-signed URL from backend for:', { filename, contentType, path });

      // IMPORTANT:
      // Backend signs presigned PUT URLs with `ContentType` and expects the upload request to send the
      // exact same `Content-Type` header. Many call sites pass `contentType` separately but don't
      // include it inside the `data` payload items. Normalize it here to avoid S3 403 (SignatureMismatch).
      const payloadData = Array.isArray(data)
        ? data.map((item) => {
          const method = String(item?.method || '').toUpperCase();
          if (method !== 'PUT') return item;
          if (item?.contentType) return item;
          const normalized = (contentType || '').trim() || 'application/octet-stream';
          return { ...item, contentType: normalized };
        })
        : data;

      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}api/user/get-presignedurl`, {
        data: payloadData
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || 'YOUR_AUTH_TOKEN'}`
        }
      });

      const presignedData = response?.data?.data;
      console.log('Full response:', response);
      console.log('Response data:', response?.data);
      console.log('Presigned data:', presignedData);

      if (!presignedData) {
        throw new Error('No data in response from backend');
      }

      if (!Array.isArray(presignedData)) {
        throw new Error(`Invalid response format. Expected array, got ${typeof presignedData}`);
      }

      if (presignedData.length === 0) {
        throw new Error('Empty array returned from backend');
      }

      return presignedData
      // {
      //   success: true,
      //   url: presignedData.url,
      //   key: presignedData.key || `${path}/${filename}`,
      //   bucket: presignedData.bucket
      // };

    } catch (error) {
      console.error('Error getting pre-signed URL from backend:', error);
      throw new Error(`Failed to get upload URL: ${error.message}`);
    }
  };

  async getDownloadUrl(data) {
    try {

      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}api/user/get-presignedurl`, {
        data
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || 'YOUR_AUTH_TOKEN'}`
        }
      });
      return response?.data?.data[0] ?? "";
    } catch (error) {
      console.error('Error getting download URL:', error);
      throw new Error('Failed to get download URL');
    }
  }

  async updatedUploadToS3(file, presignedUrl, onProgress = null, contentType = null) {
    try {
      console.log('[S3 Upload] Starting upload...');
      console.log('[S3 Upload] File details:', {
        name: file.name,
        type: file.type,
        size: file.size
      });
      console.log('[S3 Upload] Content-Type parameter:', contentType);
      console.log('[S3 Upload] Presigned URL (first 150 chars):', presignedUrl.substring(0, 150));

      // CRITICAL: The Content-Type MUST match EXACTLY what was used to generate the presigned URL
      // If contentType is provided, use it - it should be the exact value from the presigned URL response
      if (!contentType) {
        console.warn('[S3 Upload] WARNING: No contentType provided, using file.type');
      }

      // Normalize: trim whitespace and ensure it's not empty
      let fileContentType = contentType || file.type || 'application/octet-stream';
      fileContentType = fileContentType.trim();

      if (!fileContentType || fileContentType === '') {
        fileContentType = 'application/octet-stream';
      }

      console.log('[S3 Upload] Final Content-Type to use (normalized):', fileContentType);
      console.log('[S3 Upload] Content-Type comparison:', {
        provided: contentType,
        providedNormalized: contentType ? contentType.trim() : null,
        fileType: file.type,
        fileTypeNormalized: file.type ? file.type.trim() : null,
        final: fileContentType
      });

      // For S3 presigned URLs, if ContentType was specified in PutObjectCommand,
      // it becomes part of the signature and MUST match exactly
      // We need to ensure the Content-Type header matches exactly what was used to generate the presigned URL

      // Convert file to Blob with explicit type to ensure Content-Type is set correctly
      const fileBlob = file instanceof Blob ? file : new Blob([file], { type: fileContentType });

      const config = {
        headers: {
          'Content-Type': fileContentType
        },
        onUploadProgress: onProgress ? (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted, progressEvent.loaded, progressEvent.total);
        } : undefined,
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      };

      console.log('[S3 Upload] Request config:', {
        headers: config.headers,
        hasProgressCallback: !!onProgress
      });

      // Use fetch API to avoid any Axios global configuration issues (interceptors, defaults, etc.)
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': fileContentType
        },
        body: fileBlob
      });

      if (!response.ok) {
        const responseText = await response.text();
        console.error('[S3 Upload] Upload failed:', {
          status: response.status,
          statusText: response.statusText,
          responseText
        });

        // Parse XML error if possible to get meaningful message
        let errorReason = responseText;
        try {
          if (responseText.includes('<Message>')) {
            const match = responseText.match(/<Message>(.*?)<\/Message>/);
            if (match && match[1]) {
              errorReason = match[1];
            }
          } else if (responseText.includes('<Code>')) {
            const match = responseText.match(/<Code>(.*?)<\/Code>/);
            if (match && match[1]) {
              errorReason = `S3 Error Code: ${match[1]}`;
            }
          }
        } catch (e) {
          // ignore parsing error
        }

        if (response.status === 400) {
          throw new Error(`Upload failed (400): ${errorReason}`);
        }

        throw new Error(`Upload failed (${response.status}): ${errorReason}`);
      }

      console.log('[S3 Upload] Success! Status:', response.status);

      return {
        success: true,
        status: response.status,
        url: presignedUrl.split('?')[0] // Clean URL without query parameters
      };
    } catch (error) {
      console.error('[S3 Upload] ERROR:', error);
      throw error; // Re-throw as is since we formatted it above
    }
  }
  /**
   * Upload file - maintains existing interface but uses pre-signed URLs
   */
  async uploadFile(file, options = {}) {
    try {
      const {
        path = '',
        generateUniqueKey = true,
        metadata = {},
        onProgress = null
      } = options;

      // Generate filename
      let filename = file.name;
      if (generateUniqueKey) {
        const timestamp = Date.now();
        const extension = file.name.split('.').pop();
        const baseName = file.name.replace(/\.[^/.]+$/, '');
        filename = `${timestamp}-${baseName}.${extension}`;
      }

      // Get pre-signed URL
      const fileData = {
        filename,
        contentType: file.type,
        path: path || '',
        userId: metadata.userId || 'anonymous'
      };

      const presignedData = await this.getPresignedUrl(fileData);

      // Upload to S3 using pre-signed URL
      const uploadConfig = {
        headers: {
          'Content-Type': file.type
        },
        onUploadProgress: onProgress ? (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted, progressEvent.loaded, progressEvent.total);
        } : undefined
      };

      await axios.put(presignedData.url, file, uploadConfig);

      const fileUrl = presignedData.url.split('?')[0]; // Clean URL without query params

      // Return in expected format to maintain compatibility
      return {
        success: true,
        url: fileUrl,
        key: presignedData.key,
        bucket: presignedData.bucket,
        etag: 'generated-via-presigned-url'
      };

    } catch (error) {
      console.error('=== S3 UPLOAD ERROR ===');
      console.error('Upload failed:', error);
      throw new Error(`Upload failed: ${error.message}`);
    }
  }

  /**
   * Download file - maintains existing interface
   */
  async downloadFile(fileUrl, filename) {
    try {
      let downloadUrl = fileUrl;

      // If it's a key instead of URL, get download URL from backend
      if (!fileUrl.startsWith('http')) {
        const response = await axios.post(`${this.apiBaseUrl}/s3/get-download-url`, {
          key: fileUrl
        });
        downloadUrl = response.data.url;
      }

      // Download file
      const response = await axios.get(downloadUrl, {
        responseType: 'blob'
      });

      // Create blob and trigger download
      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download';
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);


      return {
        success: true,
        blob: blob,
        contentType: response.headers['content-type'],
        size: blob.size
      };

    } catch (error) {
      console.error('=== S3 DOWNLOAD ERROR ===');
      console.error('Download failed:', error);
      throw new Error(`Download failed: ${error.message}`);
    }
  }

  /**
   * Delete file - maintains existing interface
   */
  async deleteFile(fileKey) {
    try {
      console.log('Deleting file:', fileKey);

      // Extract key from URL if needed
      let key = fileKey;
      if (fileKey.includes('amazonaws.com')) {
        key = fileKey.split('amazonaws.com/')[1].split('?')[0];
      }

      await axios.delete(`${this.apiBaseUrl}/s3/delete-file`, {
        data: { key }
      });

      return {
        success: true,
        message: `File ${key} deleted successfully`
      };
    } catch (error) {
      console.error('Delete failed:', error);
      throw new Error(`Delete failed: ${error.message}`);
    }
  }

  /**
   * Validate file - keeps existing interface
   */
  validateFile(file, options = {}) {
    const {
      maxSizeInMB = 10,
      allowedExtensions = []
    } = options;

    const errors = [];
    let valid = true;

    // Size validation
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      errors.push(`File size exceeds ${maxSizeInMB}MB limit`);
      valid = false;
    }

    // Extension validation
    if (allowedExtensions.length > 0) {
      const extension = file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(extension)) {
        errors.push(`File extension .${extension} is not allowed`);
        valid = false;
      }
    }

    return {
      valid,
      error: errors.join(', '),
      errors
    };
  }

  // Maintain compatibility with existing code patterns
  async listFiles(options = {}) {
    try {
      const response = await axios.get(`${this.apiBaseUrl}/s3/list-files`, {
        params: options
      });
      return response.data;
    } catch (error) {
      console.error('List files failed:', error);
      throw new Error(`List files failed: ${error.message}`);
    }
  }

  async getFileInfo(fileKey) {
    try {
      const response = await axios.get(`${this.apiBaseUrl}/s3/file-info/${encodeURIComponent(fileKey)}`);
      return response.data;
    } catch (error) {
      console.error('Get file info failed:', error);
      throw new Error(`Get file info failed: ${error.message}`);
    }
  }
}

// Maintain existing export pattern
export const S3Utils = {
  formatFileSize: (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  getFileExtension: (filename) => {
    return filename.split('.').pop().toLowerCase();
  },

  generateUniqueFilename: (originalName, prefix = '') => {
    const timestamp = Date.now();
    const extension = S3Utils.getFileExtension(originalName);
    const baseName = originalName.replace(/\.[^/.]+$/, '');
    return `${prefix}${timestamp}-${baseName}.${extension}`;
  },

  validateFileType: (file, allowedTypes = []) => {
    if (allowedTypes.length === 0) return true;
    const fileExtension = S3Utils.getFileExtension(file.name);
    return allowedTypes.includes(fileExtension) || allowedTypes.includes(file.type);
  },

  validateFileSize: (file, maxSizeInMB = 10) => {
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    return file.size <= maxSizeInBytes;
  }
};

// Create singleton instance to maintain compatibility
const s3Helper = S3Helper.getInstance();

// Maintain existing export patterns
export { s3Helper };
export default S3Helper;
